import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/ShopContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";

export default function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, updateQuantity, removeItem, itemKey } = useCart();
  const { currency, formatAmount } = useCurrency();
  const navigate = useNavigate();
  const [productMap, setProductMap] = useState({});

  // Hydrate product info for any items not already enriched
  useEffect(() => {
    if (!drawerOpen || items.length === 0) return;
    const missing = items.map((it) => it.product_id).filter((id) => !productMap[id]);
    if (missing.length === 0) return;
    Promise.all(missing.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null)))
      .then((arr) => {
        const next = { ...productMap };
        arr.forEach((p) => { if (p) next[p.id] = p; });
        setProductMap(next);
      });
  }, [drawerOpen, items, productMap]);

  const subtotal = items.reduce((s, it) => {
    const p = productMap[it.product_id];
    if (!p) return s;
    const price = currency === "INR" ? p.price_inr : p.price_usd;
    return s + price * it.quantity;
  }, 0);

  return (
    <AnimatePresence>
      {drawerOpen && (
        <motion.div
          data-testid="cart-drawer"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm"
          onClick={() => setDrawerOpen(false)}
        >
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-0 h-full w-full sm:w-[460px] bg-midnight-deep border-l border-white/10 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-center justify-between p-6 border-b border-white/8">
              <div>
                <div className="eyebrow text-silver/70 mb-1">Your Selection</div>
                <h3 className="font-serif text-white text-2xl">The Bag</h3>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                aria-label="Close cart"
                data-testid="cart-close"
                className="text-white/70 hover:text-white"
              >
                <X size={22} strokeWidth={1.2} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-10 text-center" data-testid="cart-empty">
                  <div className="eyebrow text-silver/70 mb-3">Empty</div>
                  <p className="font-serif text-white text-2xl mb-6">Nothing chosen yet.</p>
                  <button
                    onClick={() => { setDrawerOpen(false); navigate("/"); }}
                    className="inline-flex items-center gap-3 border border-white/25 px-6 py-3 text-[11px] tracking-[0.32em] uppercase text-white hover:border-white"
                  >
                    Explore the edits
                  </button>
                </div>
              ) : (
                <ul className="divide-y divide-white/8">
                  {items.map((it) => {
                    const p = productMap[it.product_id];
                    const key = itemKey(it);
                    const price = p ? (currency === "INR" ? p.price_inr : p.price_usd) : 0;
                    return (
                      <li key={key} className="flex gap-4 p-5" data-testid={`cart-item-${it.product_id}`}>
                        <div className="w-24 h-32 bg-midnight-panel overflow-hidden shrink-0">
                          {p && <img src={p.image} alt={p.name} className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 flex flex-col justify-between min-w-0">
                          <div>
                            <Link
                              to={`/product/${it.product_id}`}
                              onClick={() => setDrawerOpen(false)}
                              className="font-serif text-white text-lg leading-tight hover:text-silver"
                            >
                              {p ? p.name : it.product_id}
                            </Link>
                            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55 mt-1">
                              {it.size && <span className="mr-3">Size · {it.size}</span>}
                              {it.color && <span>{it.color}</span>}
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <div className="inline-flex items-center border border-white/15">
                              <button
                                onClick={() => updateQuantity(key, it.quantity - 1)}
                                aria-label="Decrease"
                                className="px-2.5 py-1.5 text-white/70 hover:text-white"
                                data-testid={`qty-dec-${it.product_id}`}
                              >
                                <Minus size={12} />
                              </button>
                              <span className="px-3 text-white text-sm">{it.quantity}</span>
                              <button
                                onClick={() => updateQuantity(key, it.quantity + 1)}
                                aria-label="Increase"
                                className="px-2.5 py-1.5 text-white/70 hover:text-white"
                                data-testid={`qty-inc-${it.product_id}`}
                              >
                                <Plus size={12} />
                              </button>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-silver text-sm tracking-[0.2em]">
                                {p ? formatAmount(price * it.quantity) : "—"}
                              </span>
                              <button
                                onClick={() => removeItem(key)}
                                aria-label="Remove"
                                data-testid={`cart-remove-${it.product_id}`}
                                className="text-white/40 hover:text-white"
                              >
                                <Trash2 size={14} strokeWidth={1.2} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <footer className="border-t border-white/10 p-6 space-y-4">
                <div className="flex justify-between text-white">
                  <span className="text-[10px] tracking-[0.32em] uppercase text-silver/70">Subtotal</span>
                  <span className="text-lg" data-testid="cart-subtotal">{formatAmount(subtotal)}</span>
                </div>
                <p className="text-[10px] tracking-[0.25em] uppercase text-white/40">
                  Taxes and shipping calculated at checkout.
                </p>
                <button
                  onClick={() => { setDrawerOpen(false); navigate("/checkout"); }}
                  data-testid="cart-checkout-btn"
                  className="w-full flex items-center justify-center gap-3 bg-white text-black py-4 uppercase tracking-[0.32em] text-[11px] hover:bg-silver transition-colors"
                >
                  Proceed to Checkout
                  <ArrowRight size={14} strokeWidth={1.4} />
                </button>
              </footer>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
